import * as React from "react";
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { firebaseStorage } from "../../firebase";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginImageResize from 'filepond-plugin-image-resize';
import i18next from "../../i18n";
import { useTranslation } from "react-i18next";

import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import "./filepond.css";
import Box from "@mui/material/Box";
import { useState } from "react";

registerPlugin(
    FilePondPluginImageExifOrientation,
    FilePondPluginImagePreview,
    FilePondPluginFileValidateType,
    FilePondPluginFileValidateSize,
    FilePondPluginImageResize
);

export default function FileInput({
    onRequestSave = () => {},
    onRequestStart = () => {},
    onRequestEnd = () => {},
    folder = "images",
    prefix = "",
    acceptedFileTypes = ['image/*'],
    defaultFiles = [],
    label = i18next.t('dragNewFile'),
    allowMultiple = false,
    maxFiles = 1,
    showLoadedImages = true,
    allowFileSizeValidation = false,
    maxFileSize,
    labelMaxFileSizeExceeded,
    labelMaxFileSize,
    allowImageResize = false,
    imageResizeTargetWidth,
    imageResizeTargetHeight,
}) {
    const { t } = useTranslation();

    const translation = {
        labelIdle: label + " " + t('or') + " " + "<span class='filepond--label-action'>" + t("browse") + "</span>",
        labelInvalidField: t('invalidFile'),
        labelFileWaitingForSize: t('waitingForSize'),
        labelFileSizeNotAvailable: t('sizeNotAvailable'),
        labelFileCountSingular: 'file in list',
        labelFileCountPlural: 'files in list',
        labelFileLoading: t('loading'),
        labelFileAdded: t('added'),
        labelFileLoadError: t('loadError'),
        labelFileRemoved: t('removed'),
        labelFileRemoveError: t('removeError'),
        labelFileProcessing: t('loading'),
        labelFileProcessingComplete: t('loadingComplete'),
        labelFileProcessingAborted: t('loadingAborted'),
        labelFileProcessingError: t('loadingError'),
        labelFileProcessingRevertError: t('loadingRevertError'),
        labelTapToCancel: t('tapToCancel'),
        labelTapToRetry: t('tapToRetry'),
        labelTapToUndo: t('tapToUndo'),
        labelButtonRemoveItem: t('remove'),
        labelButtonAbortItemLoad: t('abort'),
        labelButtonRetryItemLoad: t('retry'),
        labelButtonAbortItemProcessing: t('abort'),
        labelButtonUndoItemProcessing: t('abort'),
        labelButtonRetryItemProcessing: t('retry'),
        labelButtonProcessItem: t('load'),
    };

    const [files, setFiles] = React.useState(defaultFiles);
    const [loading, setLoading] = useState(false);

    const requestSave = (...props) => { setLoading(false); onRequestSave(...props); };
    const requestStart = (...props) => { setLoading(true); onRequestStart(...props); };
    const requestEnd = (...props) => { setLoading(false); onRequestEnd(...props); };

    const server = {
        // Upload su Firebase Storage
        process: (fieldName, file, metadata, load, error, progress, abort) => {
            const path = `${folder}/${prefix}/${new Date().getTime()}`;
            const fileRef = storageRef(firebaseStorage, path);
            const task = uploadBytesResumable(fileRef, file);

            task.on(
                'state_changed',
                snap => {
                    progress(true, snap.bytesTransferred, snap.totalBytes);
                },
                err => {
                    requestEnd();
                    error(err.message);
                },
                () => {
                    requestEnd();
                    load(path);
                    getDownloadURL(fileRef).then(url => {
                        if (!showLoadedImages) {
                            setFiles([]);
                        }
                        requestSave(url);
                    });
                }
            );

            return {
                abort: () => {
                    task.cancel();
                    abort();
                }
            };
        },

        // Carica un file già presente su Firebase Storage
        load: (source, load, error, progress, abort) => {
            progress(true, 0, 1024);
            const fileRef = storageRef(firebaseStorage, source);
            getDownloadURL(fileRef)
                .then(url => {
                    let xhr = new XMLHttpRequest();
                    xhr.responseType = 'blob';
                    xhr.onload = () => load(xhr.response);
                    xhr.open('GET', url);
                    xhr.send();
                })
                .catch(err => {
                    requestEnd();
                    error(err.message);
                    abort();
                });
        },

        // Elimina il file da Firebase Storage
        revert: (uniqueFileId, load) => {
            const fileRef = storageRef(firebaseStorage, uniqueFileId);
            deleteObject(fileRef).catch(() => {});
            load();
            requestSave(null);
        }
    };

    return (
        <Box
            sx={allowMultiple && loading && {
                '& .filepond--root .filepond--drop-label, .filepond--root .filepond--drop-label label': {
                    height: '2em !important',
                    minHeight: '2em !important'
                }
            }}
        >
            <FilePond
                files={files}
                acceptedFileTypes={acceptedFileTypes}
                allowMultiple={allowMultiple}
                maxFiles={maxFiles}
                onupdatefiles={fileItems => setFiles(fileItems.map(fi => fi.file))}
                server={server}
                {...translation}
                onaddfilestart={requestStart}
                allowFileSizeValidation={allowFileSizeValidation}
                maxFileSize={maxFileSize}
                labelMaxFileSizeExceeded={labelMaxFileSizeExceeded}
                labelMaxFileSize={labelMaxFileSize}
                allowImageResize={allowImageResize}
                imageResizeTargetWidth={imageResizeTargetWidth}
                imageResizeTargetHeight={imageResizeTargetHeight}
                imageResizeUpscale={false}
            />
        </Box>
    );
}